import { lazy, ReactElement, Suspense, SyntheticEvent, useContext, useEffect, useState } from "react";
import {
  IonIcon,
  IonModal,
  IonTitle,
  IonToolbar,
  useIonRouter,
} from "@ionic/react";
import {
  closeOutline,
  personOutline,
  logOutOutline,
  shareOutline,
  bugOutline,
  informationCircleOutline,
} from "ionicons/icons";
import { useTranslation } from "react-i18next";
import styled, { css, keyframes } from "styled-components";
import Icon from "../../atoms/Icon";
import AuthContext from "../../../contexts/AuthContext";
import { useStudySlug } from "../../../hooks/useStudySlug";
import { getStudyTabIds } from "../../../navigation/studyTabs";
import MenuItem from "./MenuItem";
import LanguageSelectorModal from "../LanguageSelector/LanguageSelectorModal/LanguageSelectorModal";
import ReactCountryFlag from "react-country-flag";
import { langToCountryCode } from "../../../helper/lang";
import { resetCaches } from "../../../services/questionnaire.service";
import { getProjectConfig } from "../../../projects";
import { settingsOutline } from "ionicons/icons";
import ReportBugModal from "../../../pages/Profile/ReportBugModal";
import AboutModal from "../../../pages/Profile/AboutModal";
import { useConfig } from "../../../contexts/ConfigContext";
import { getInvitations, getInvitPubLink } from "../../../services/invitation.service";
import { InviteRelativeModal } from "../../organisms/Invitation/InviteModals";
import IconProfile from "../../../assets/MentalPlus/Profil.svg";
import IconSettings from "../../../assets/MentalPlus/Paramètres.svg";
import IconDeconnexion from "../../../assets/MentalPlus/Déconnexion.svg";
import IconEquipe from "../../../assets/MentalPlus/Equipe.svg";
import IconContact from "../../../assets/MentalPlus/Contact.svg";
import IconPartager from "../../../assets/MentalPlus/Partager.svg";
import{
  InvitationChannelEnum,
  InvitationConfigType,
} from "../../../types/invitation.types";

// Island settings belong to the lazy island-map experience — only island-map
// studies show the menu entry / load the chunk.
const IslandSettings = lazy(() =>
  import("../../../pages/MentalPlusPages").then((m) => ({
    default: m.IslandSettings,
  }))
);

interface MenuProps {
  isConnected: boolean;
  onClose(): void;
}

const Menu = ({ isConnected, onClose }: MenuProps): ReactElement => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const study_slug = useStudySlug();
  // The drawer docks on whichever side the project's header puts the burger
  // (Mental+ left, default right): the panel should emerge from under the
  // finger that summoned it, not from the opposite corner. Page overrides
  // never move the burger, so the PROJECT layout is the right source.
  const menuSide: "left" | "right" = getProjectConfig(study_slug).header?.left?.includes("menu")
    ? "left"
    : "right";
  const { user, endSession } = useContext(AuthContext);
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [reportBugOpen, setReportBugOpen] = useState(false);
  const { study } = useConfig();
  const ionRouter = useIonRouter();

  const stopPropagation = (e: SyntheticEvent) => {
    e.stopPropagation();
  };

  // "Profil" leads to the Compte tab — the single account screen. It used to open
  // a second, parallel account UI (pages/Profile/Profile) that Compte had already
  // outgrown; two screens editing the same fields only drift apart. Guarded on the
  // tab existing so the row can never lead nowhere for a project without it.
  const hasAccountTab = getStudyTabIds(study_slug).includes("account");
  const openAccountTab = () => {
    onClose();
    ionRouter.push(`/${study_slug}/tabs/account`);
  };

  const logOut = async () => {
    await endSession();
    resetCaches();
    ionRouter.push(`/${study_slug}/welcome`, "root", "replace", {
      unmount: true,
    });
  };

  const handleShare = async () => {
    if (!study) return;

    try {
      const { data: invitationConfigs } = await getInvitations({
        studyId: study.id,
        lang: language,
      });

      // A shareable link lets someone join exactly like an invitation does, so it obeys
      // the same per-dossier limit: a role the dossier is already full of is not offered.
      const publicLinkConfig = (invitationConfigs as InvitationConfigType[]).find(
        (config) =>
          config.channel === InvitationChannelEnum.PUBLICLINK && !config.to_role_already_joined
      );

      if (!publicLinkConfig) {
        alert(t("menu_share_no_public_link"));
        return;
      }

      const { data: link } = await getInvitPubLink(
        { studyId: study.id },
        { invitation_config_id: publicLinkConfig.id }
      );

      const shareUrl = link as string;

      if (navigator.share) {
        try {
          await navigator.share({
            title: t("menu_share_title"),
            text: t("menu_share_text"),
            url: shareUrl,
          });
        } catch (err) {
          console.error("Share aborted or failed:", err);
        }
      } else {
        try {
          await navigator.clipboard?.writeText(shareUrl);
          alert(t("menu_share_link_copied"));
        } catch (err) {
          alert(t("menu_share_not_supported"));
          console.error("Clipboard share failed:", err);
        }
      }
    } catch (e) {
      console.error("Error while generating invitation token:", e);
      alert(t("menu_share_link_error"));
    }
  };
const [isAboutOpen, setIsAboutOpen] = useState(false);
const handleAbout = () => {
  setIsAboutOpen(true);
};

  // « Inviter … » appears only when the researcher has authored an email invitation FROM
  // this participant's role — that is how a parent gets to declare the second parent
  // without the app hard-coding anything about parents. The role's own label is the
  // button text, so the same affordance reads correctly in any study.
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteRoleLabel, setInviteRoleLabel] = useState<string | null>(null);
  // The share entry hides on the same rule rather than opening and then apologising.
  // Starts true so the affordance is never withheld while the roles are still loading.
  const [canShare, setCanShare] = useState(true);

  useEffect(() => {
    if (!isConnected || !study?.id) return;
    let cancelled = false;
    getInvitations({ studyId: study.id, lang: language })
      .then(({ data }) => {
        if (cancelled) return;
        // Nothing to offer once that role has already joined the dossier — the second
        // parent who registered months ago should not still be "invitable".
        const emailConfig = (data as InvitationConfigType[]).find(
          (config) =>
            config.channel === InvitationChannelEnum.EMAIL && !config.to_role_already_joined
        );
        setInviteRoleLabel(emailConfig ? emailConfig.to_role_label ?? emailConfig.to_role : null);
        setCanShare(
          (data as InvitationConfigType[]).some(
            (config) =>
              config.channel === InvitationChannelEnum.PUBLICLINK && !config.to_role_already_joined
          )
        );
      })
      .catch(() => {
        /* fail-soft: the entry simply stays hidden */
      });
    return () => {
      cancelled = true;
    };
  }, [isConnected, study?.id, language]);

  return (
    <Modal $side={menuSide} onClick={onClose}>
      <MenuList $side={menuSide} onClick={stopPropagation}>
        <Header>
          <Icon icon={closeOutline} size="large" onClick={onClose} />
        </Header>

        <div>
          {isConnected && hasAccountTab && (
            <MenuItem
              icon={<MaskIcon $src={IconProfile} aria-hidden="true" />}
              title={t("mentalplus_menu_profile")}
              action={t("common_open")}
              onClick={openAccountTab}
            />
          )}

          {isConnected && getProjectConfig(study_slug).homeExperience === "island-map" && (
            <MenuItem
             icon={<MaskIcon $src={IconSettings} aria-hidden="true" />}
              title={t("mentalplus_menu_island_settings")}
              action={t("common_open")}
              onClick={() => setSettingsOpen(true)}
            />
          )}

          <MenuItem
            icon={
              <ReactCountryFlag
                countryCode={
                  langToCountryCode[language] || language.toUpperCase()
                }
                svg
                style={{
                  width: "1.5em",
                  height: "1.5em",
                  borderRadius: "3px",
                  objectFit: "cover",
                }}
                title={language}
              />
            }
            title={t(`lang_${language}`)}
            action={t("menu_language_button_text")}
            onClick={() => setModalVisible(true)}
          />

          {isConnected && inviteRoleLabel && (
            <MenuItem
              icon={<MaskIcon $src={IconPartager} aria-hidden="true" />}
              title={t("invite_relative_menu_title", { role: inviteRoleLabel })}
              action={t("invite_relative_menu_action")}
              onClick={() => setInviteOpen(true)}
            />
          )}

          {canShare && (
            <MenuItem
              icon={<MaskIcon $src={IconPartager} aria-hidden="true" />}
              title={t("mentalplus_menu_share")}
              action={t("menu_share")}
              onClick={handleShare}
            />
          )}

          {isConnected && (
            <MenuItem
              icon={<MaskIcon $src={IconContact} aria-hidden="true" />}
              title={t("mentalplus_menu_conatct")}
              action={t("common_open")}
              onClick={() => setReportBugOpen(true)}
            />
          )}

          <MenuItem
            icon={<MaskIcon $src={IconEquipe} aria-hidden="true" />}
            title={t("mentalplus_menu_equipe")}
            action={t("common_open")}
            onClick={handleAbout}
          />

          {isConnected && (
            <MenuItem
              icon={<MaskIcon $src={IconDeconnexion} aria-hidden="true" />}
              title={user?.attributes?.firstname || t("menu_logOut")}
              action={t("menu_logOut")}
              onClick={() => logOut().then().catch()}
            />
          )}

          <LanguageSelectorModal
            modalVisible={modalVisible}
            onCloseClick={() => setModalVisible(false)}
          />

          <InviteRelativeModal
            isOpen={inviteOpen}
            onDismiss={() => setInviteOpen(false)}
            onSent={() => {
              setInviteOpen(false);
              alert(t("invite_relative_done"));
            }}
          />

          {getProjectConfig(study_slug).homeExperience === "island-map" && (
            <IonModal
              className="resonance-sheetmodal"
              isOpen={settingsOpen}
              backdropDismiss={true}
              breakpoints={[0, 1]}
              initialBreakpoint={1}
              canDismiss={true}
              onDidDismiss={() => setSettingsOpen(false)}
            >
              <IonToolbar>
                <IonTitle>Island Setting</IonTitle>
                <CloseButton slot="end" onClick={() => setSettingsOpen(false)}>
                  ×
                </CloseButton>
              </IonToolbar>

              <Suspense fallback={null}>
                <IslandSettings />
              </Suspense>
            </IonModal>
          )}

          <ReportBugModal
            isOpen={reportBugOpen}
            onDismiss={() => setReportBugOpen(false)}
            user={user}
            study={study}
          />
          <AboutModal
            isOpen={isAboutOpen}
            onDismiss={() => setIsAboutOpen(false)}
          />
        </div>
      </MenuList>
    </Modal>
  );
};

const Modal = styled.div<{ $side: "left" | "right" }>`
  position: absolute;
  height: 100vh;
  width: 100vw;
  background-color: rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: ${({ $side }) => ($side === "left" ? "flex-start" : "flex-end")};
  z-index: 99999999;
`;

/* Same 70% slide the drawer always had, mirrored per docking side. */
const slideFromRight = keyframes`
  from {
    margin-right: -70%;
  }
  to {
    margin-right: 0;
  }
`;

const slideFromLeft = keyframes`
  from {
    margin-left: -70%;
  }
  to {
    margin-left: 0;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 10;
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
`;

const MenuList = styled.div<{ $side: "left" | "right" }>`
  width: 70%;
  max-width: 400px;
  animation: ${({ $side }) =>
    css`${$side === "left" ? slideFromLeft : slideFromRight} 0.1s ease`};
  background-color: var(--app-color-background, white);
`;

/* Header band follows the toolbar rule: primary (chrome) with its own
   contrast ink, so the ink can never land unreadably on the band color under
   custom themes. */
const Header = styled.div`
  height: 70px;
  background-color: var(--app-color-primary, #94f1ef);
  color: var(--app-color-primary-contrast, #041723);
  display: flex;
  align-items: center;
`;

/* Monochrome menu glyphs ship as #4285F4-filled SVGs; an <img> can't be
   recolored by CSS, so paint the accent token (interactive link icons —
   tokens.ts roles) through the svg's alpha via mask. Badge-style glyphs
   (Déconnexion) knock their symbol out of the disc rather than drawing it in
   white, so they mask too. Only genuinely multicolor art (country flags) stays
   a plain <img> — a mask would flatten it to one color. */
const MaskIcon = styled.span<{ $src: string }>`
  display: inline-block;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  background-color: var(--app-color-accent, #4285f4);
  -webkit-mask-image: url("${({ $src }) => $src}");
  mask-image: url("${({ $src }) => $src}");
  -webkit-mask-size: contain;
  mask-size: contain;
  -webkit-mask-position: center;
  mask-position: center;
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
`;

export default Menu;